/* eslint-disable react-hooks/exhaustive-deps */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { usePrevious } from 'react-use'
import YouTube, { YouTubeEvent, YouTubePlayer } from 'react-youtube'
import classNames from 'classnames'

import SVGIcon from '@/components/base/SVGIcon'
import VideoTimeline from '@/components/base/YoutubeVideoTimeline'
import useYoutubeThumbnail from '@/hooks/useYoutubeThumbnail'

import { WPImage } from '@/types'

type YoutubeVideoHandler = {
  play: () => void
}

type YoutubeVideoProps = Partial<{
  videoId: string
  poster: WPImage
  className: string
  autoplay: boolean
}>

const YoutubeVideo = forwardRef<YoutubeVideoHandler, YoutubeVideoProps>(
  ({ videoId, autoplay, poster, className }, ref) => {
    const [isPlaying, setPlaying] = useState(false)
    const [isFirst, setFirst] = useState(true)
    const [isLoading, setLoading] = useState(false)
    const video = useRef<YouTube>(null)
    const videoPlayer = useRef<YouTubePlayer>(null)
    const posterImg = useYoutubeThumbnail(videoId!)

    const opts = useRef({
      playerVars: {
        autoplay: 0,
        controls: 0,
        showinfo: 0,
        color: 'white',
        rel: 0,
        fs: 0,
        modestbranding: 1,
        playsinline: 1
      },
      host: 'https://www.youtube-nocookie.com'
    })

    const onClickPlay = () => {
      if (videoPlayer.current) {
        if (isFirst) {
          videoPlayer.current.playVideo()
          videoPlayer.current.mute()
          setLoading(true)
        } else {
          videoPlayer.current.playVideo()
          videoPlayer.current.unMute()
          setPlaying(true)
        }
      }
    }

    useImperativeHandle(ref, () => ({
      play: onClickPlay
    }))

    const prevIsFirst = usePrevious(isFirst)
    useEffect(() => {
      if (prevIsFirst && !isFirst) {
        onClickPlay()
        setLoading(false)
      }
    }, [isFirst])

    const onReady = (event: YouTubeEvent<any>) => {
      videoPlayer.current = event.target
      if (autoplay) onClickPlay()
    }

    const onStateChange = (event: YouTubeEvent<any>) => {
      if (event.target.getPlayerState() === 1 && isFirst) {
        setTimeout(() => videoPlayer.current.pauseVideo(), 100)
        setTimeout(() => videoPlayer.current.seekTo(0), 200)
        setTimeout(() => setFirst(false), 3000)
      }
    }

    const onClickPause = () => {
      if (videoPlayer.current) setPlaying(false)
    }

    const onEnd = () => {
      videoPlayer.current.seekTo(0)
      onClickPause()
    }

    const prevIsPlaying = usePrevious(isPlaying)
    useEffect(() => {
      if (prevIsPlaying && !isPlaying)
        setTimeout(() => videoPlayer.current.pauseVideo(), 300)
    }, [isPlaying])

    return (
      <div
        className={classNames(
          'relative h-full w-full',
          '[&_iframe]:pointer-events-none [&_iframe]:absolute [&_iframe]:left-0 [&_iframe]:top-0 [&_iframe]:z-0 [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:rounded-xxs [&_iframe]:shadow-3xl',
          className
        )}
      >
        <YouTube
          ref={video}
          videoId={videoId}
          opts={opts.current}
          onReady={onReady}
          onStateChange={onStateChange}
          onEnd={onEnd}
        />

        <div
          style={{ backgroundImage: `url(${poster?.url || posterImg})` }}
          className={classNames(
            'absolute left-0 top-0 z-20 h-full w-full rounded-xxs',
            'bg-cover bg-center bg-no-repeat',
            'transition-opacity duration-300 ease-out-cubic',
            'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-xxs before:bg-black/30',
            {
              'pointer-events-none opacity-0': isPlaying
            }
          )}
        >
          <button
            className={classNames(
              'absolute left-1/2 top-1/2 h-[7.6rem] w-[7.6rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white',
              'transition-opacity duration-300 ease-out-cubic',
              {
                'pointer-events-none opacity-0': isLoading
              }
            )}
            aria-label="Play"
            onClick={onClickPlay}
          >
            <SVGIcon
              symbol="play"
              className="absolute left-1/2 top-1/2 h-[1.6rem] w-[1.4rem] -translate-x-1/2 -translate-y-1/2 fill-white stroke-white"
            />
          </button>
        </div>

        {videoPlayer.current && (
          <div
            className={classNames(
              'absolute left-0 top-0 z-10 h-full w-full rounded-xxs p-[4rem_2rem_2rem_3rem]',
              'bg-gradient-to-t from-black/5 from-0% to-black/0 to-20%'
            )}
          >
            <VideoTimeline
              video={videoPlayer.current}
              isPlaying={isPlaying}
              onClickPause={onClickPause}
            />
          </div>
        )}
      </div>
    )
  }
)

export default YoutubeVideo
